import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { CONSENT_ENABLED, ANALYTICS_PROVIDER_KEY, GTM_CONTAINER_ID } from '../operationalConfig';
import { loadAnalytics } from '../analytics';
import { setupConsentModeDefault, withdrawAnalyticsConsentMode } from '../gtm';

// WP-3.1 — RGPD consent (the gating layer).
//
// Categories: `necessary` (always on, no consent required) + `analytics` (opt-in).
// No advertising category. Privacy-default = analytics DENIED until the user
// explicitly grants. First-party storage only (no PII, no third party). Withdrawal
// supported (resetConsent). Owns no identity/content (ADR-0009 consumer).
//
// Prerender/SSR-safe (WP-2): all browser access is guarded, so the module imports
// and renders without `window`. Analytics is only ever *attempted* on grant, and
// is dark today (no provider wired) — proving "consent precedes analytics".

const STORAGE_KEY = 'bm_consent_v1';
const CONSENT_VERSION = 1;
const DEFAULT_STATE = 'unset'; // 'unset' | 'granted' | 'denied'

const ConsentContext = createContext(null);

// --- pure helpers (unit-tested) ---
export function parseStoredConsent(raw) {
  if (!raw) return DEFAULT_STATE;
  try {
    const o = JSON.parse(raw);
    if (o && (o.analytics === 'granted' || o.analytics === 'denied') && o.v === CONSENT_VERSION) {
      return o.analytics;
    }
  } catch {
    /* malformed → treat as undecided */
  }
  return DEFAULT_STATE;
}

export function serializeConsent(analytics, ts) {
  return JSON.stringify({ analytics, v: CONSENT_VERSION, ts });
}

// --- guarded first-party storage ---
function readStorage() {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}
function writeStorage(value) {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    window.localStorage.setItem(STORAGE_KEY, value);
  } catch {
    /* storage unavailable → in-memory only */
  }
}
function clearStorage() {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export function ConsentProvider({ children }) {
  const [analyticsConsent, setAnalyticsConsent] = useState(() => parseStoredConsent(readStorage()));

  // Consent precedes analytics: only ever attempt the (dark) loader when granted.
  // Consent Mode v2 (OPS-2): establish the denied-by-default state once at mount (no network).
  useEffect(() => {
    setupConsentModeDefault();
  }, []);

  // Consent precedes analytics: load GTM (OPS-4) only when granted; withdraw on deny.
  useEffect(() => {
    if (analyticsConsent === 'granted') {
      loadAnalytics({ analyticsConsent, provider: ANALYTICS_PROVIDER_KEY, id: GTM_CONTAINER_ID });
    } else if (analyticsConsent === 'denied') {
      withdrawAnalyticsConsentMode();
    }
  }, [analyticsConsent]);

  const grantAnalytics = useCallback(() => {
    setAnalyticsConsent('granted');
    writeStorage(serializeConsent('granted', Date.now()));
  }, []);
  const denyAnalytics = useCallback(() => {
    setAnalyticsConsent('denied');
    writeStorage(serializeConsent('denied', Date.now()));
  }, []);
  const resetConsent = useCallback(() => {
    setAnalyticsConsent(DEFAULT_STATE);
    clearStorage();
  }, []);

  const value = useMemo(
    () => ({
      analyticsConsent,
      decided: analyticsConsent !== DEFAULT_STATE,
      consentEnabled: CONSENT_ENABLED,
      grantAnalytics,
      denyAnalytics,
      resetConsent,
    }),
    [analyticsConsent, grantAnalytics, denyAnalytics, resetConsent],
  );

  return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>;
}

export function useConsent() {
  const ctx = useContext(ConsentContext);
  if (!ctx) throw new Error('useConsent must be used within a ConsentProvider');
  return ctx;
}
